import { useCallback, useEffect, useRef, useState } from "react";
import { getApiErrorMessage } from "../../../../common/utils/apiError";
import { registerSessionTeardown } from "../../../../common/utils/sessionTeardown";
import { HistoryListParams, HistoryPage } from "../../entities/history";

/**
 * The whole of one `/history/*` listing, loaded up front and remembered.
 *
 * The endpoints are page-numbered, but the table's column filters are
 * client-side and build their options from the rows they are given — a
 * Client filter over a half-loaded list offers half the clients. Infinite
 * scroll was tried and dropped for exactly that reason. So this hook walks
 * every page: the first one (a large page) renders at once, the rest are
 * fetched a few at a time in parallel and appended as they land, with
 * `loadingRest`/`loaded`/`totalCount` exposed so the listing can say the
 * filters are not complete yet.
 *
 * **Cache.** History's tabs and detail pages are sibling routes, so opening a
 * record or switching tabs unmounts the listing and would refetch all of it
 * on the way back — eleven requests for a company's services. Loaded lists
 * are therefore kept in module memory, keyed by endpoint and subject. A
 * remount renders the cached rows at once and revalidates quietly with one
 * page-1 request: if the total and the newest row match, nothing else
 * happens; otherwise the whole list is refetched and swapped in when
 * complete. A list older than `CACHE_TTL_MS` is treated as absent (full
 * refetch behind the cached rows), so edits made elsewhere that change
 * neither the count nor the newest record surface within a few minutes —
 * unless the list passes `rowVersion`, which also compares page 1 row by row.
 * `reload()` — what regenerate and delete call — bypasses the cache, and
 * `invalidateHistoryList()` drops a listing for screens that change its
 * records while it is unmounted (a lab panel approved from its detail).
 *
 * A request id guards against a slow reply for an abandoned subject
 * overwriting the current rows; appended rows are de-duped by id, since a
 * record created between two page requests shifts the later page by one.
 */

/** Rows per request. The backend accepts any size. */
export const HISTORY_FETCH_PAGE_SIZE = 500;
/** Pages fetched concurrently once the first page has said how many exist. */
const CONCURRENCY = 4;
/** 100 × 500 is well past any real company's history, and bounds a bad total. */
const MAX_PAGES = 100;
/** Lists remembered at once — the three tabs for a couple of subjects. */
const CACHE_MAX_ENTRIES = 8;
/** After this, a cached list is refetched in full rather than revalidated. */
const CACHE_TTL_MS = 5 * 60 * 1000;

interface CacheEntry {
  rows: any[];
  totalCount: number | null;
  truncated: boolean;
  /** `Date.now()` when the list finished loading. */
  storedAt: number;
}

const cache = new Map<string, CacheEntry>();

// The lists are one user's view of their own data: gone with the session.
registerSessionTeardown(() => cache.clear());

const remember = (key: string, entry: CacheEntry) => {
  // Re-insert so the map's order is least-recently-written first.
  cache.delete(key);
  cache.set(key, entry);
  while (cache.size > CACHE_MAX_ENTRIES) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) break;
    cache.delete(oldest);
  }
};

/**
 * Forgets every cached subject of one listing (client and company scope), so
 * its next mount loads it in full instead of serving rows that are now wrong.
 */
export const invalidateHistoryList = (cacheKey: string) => {
  Array.from(cache.keys())
    .filter((key) => key.startsWith(`${cacheKey}|`))
    .forEach((key) => cache.delete(key));
};

export interface HistoryListOptions<TRaw> {
  fetchPage: (params: HistoryListParams) => Promise<HistoryPage<TRaw> | null>;
  /** Names the endpoint in the cache key, e.g. "services". */
  cacheKey: string;
  /** Narrows to one client; `undefined`/`null` lists the whole company. */
  userid: string | null | undefined;
  /** False while the subject is still resolving — nothing is fetched. */
  enabled?: boolean;
  rowId?: (row: TRaw) => string;
  /**
   * What must match for a cached row to count as unchanged on revalidation,
   * e.g. a status. Without it only the count and the newest id are compared.
   */
  rowVersion?: (row: TRaw) => string;
}

export interface HistoryListResult<TRaw> {
  rows: TRaw[];
  /** First page in flight — nothing to show yet. */
  loading: boolean;
  /** Later pages still arriving; the rows and filter options are incomplete. */
  loadingRest: boolean;
  /** Rows across all pages, as reported by the backend. */
  totalCount: number | null;
  /** True when the page cap cut the listing short. */
  truncated: boolean;
  error: string | null;
  /** Fresh from the backend, ignoring the cache. Resolves with the rows. */
  reload: () => Promise<TRaw[]>;
}

const defaultRowId = (row: any): string => String(row?.id ?? "");

const dedupeAppend = <TRaw,>(
  current: TRaw[],
  more: TRaw[],
  rowId: (row: TRaw) => string
): TRaw[] => {
  const seen = new Set(current.map(rowId));
  return [...current, ...more.filter((row) => !seen.has(rowId(row)))];
};

const useHistoryList = <TRaw,>({
  fetchPage,
  cacheKey,
  userid,
  enabled = true,
  rowId = defaultRowId,
  rowVersion,
}: HistoryListOptions<TRaw>): HistoryListResult<TRaw> => {
  const scopeKey = enabled ? userid ?? "" : null;
  const fullKey = scopeKey === null ? null : `${cacheKey}|${scopeKey}`;
  const initial = fullKey ? cache.get(fullKey) : undefined;

  const [rows, setRows] = useState<TRaw[]>(initial?.rows ?? []);
  const [totalCount, setTotalCount] = useState<number | null>(
    initial?.totalCount ?? null
  );
  // True until the first page is in — including while the subject is still
  // resolving (`enabled` false), so the table shows its skeleton rather than
  // an empty state that reads as "no records".
  const [loading, setLoading] = useState(!initial);
  const [loadingRest, setLoadingRest] = useState(false);
  const [truncated, setTruncated] = useState(initial?.truncated ?? false);
  const [error, setError] = useState<string | null>(null);

  const requestIdRef = useRef(0);
  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;
  const rowIdRef = useRef(rowId);
  rowIdRef.current = rowId;
  const rowVersionRef = useRef(rowVersion);
  rowVersionRef.current = rowVersion;

  const loadAll = useCallback(
    async (force = false): Promise<TRaw[]> => {
      const requestId = ++requestIdRef.current;
      const isCurrent = () => requestId === requestIdRef.current;
      setError(null);
      setLoadingRest(false);

      if (scopeKey === null || fullKey === null) {
        // Waiting for the subject: keep the skeleton up.
        setRows([]);
        setTotalCount(null);
        setTruncated(false);
        setLoading(true);
        return [];
      }

      const params = (page: number): HistoryListParams => ({
        page,
        rows: HISTORY_FETCH_PAGE_SIZE,
        userid: scopeKey || undefined,
      });

      const cached = force ? undefined : cache.get(fullKey);
      // Stale entries still render at once, but are refetched whole below
      // instead of trusting a count-and-newest check.
      const fresh = Boolean(cached) && Date.now() - cached!.storedAt < CACHE_TTL_MS;
      if (cached) {
        // Serve what we have; the request below only checks it is still right.
        setRows(cached.rows);
        setTotalCount(cached.totalCount);
        setTruncated(cached.truncated);
        setLoading(false);
      } else {
        setLoading(true);
      }

      let all: TRaw[] = [];
      try {
        const first = await fetchPageRef.current(params(1));
        if (!isCurrent()) return [];
        all = Array.isArray(first?.data) ? first!.data : [];
        const total =
          typeof first?.totalCount === "number" ? first.totalCount : all.length;

        if (cached && fresh) {
          const newestUnchanged =
            all.length === Math.min(cached.rows.length, HISTORY_FETCH_PAGE_SIZE) &&
            (all.length === 0 ||
              rowIdRef.current(all[0]) === rowIdRef.current(cached.rows[0]));
          const version = rowVersionRef.current;
          const cachedVersions = version
            ? new Map(cached.rows.map((row) => [rowIdRef.current(row), version(row)]))
            : null;
          const versionsUnchanged =
            !version ||
            all.every((row) => cachedVersions!.get(rowIdRef.current(row)) === version(row));
          if (total === cached.totalCount && newestUnchanged) {
            if (versionsUnchanged) return cached.rows;
            if (total <= all.length) {
              // Page 1 is the whole list: swap it in without refetching.
              setRows(all);
              remember(fullKey, {
                rows: all,
                totalCount: total,
                truncated: false,
                storedAt: Date.now(),
              });
              return all;
            }
          }
        } else if (!cached) {
          // Nothing on screen yet: show page 1 while the rest arrives. A stale
          // cached list keeps showing until the full refetch swaps it in.
          setRows(all);
          setTotalCount(total);
          setLoading(false);
        }

        const pageCount = Math.ceil(total / HISTORY_FETCH_PAGE_SIZE);
        const lastPage = Math.min(pageCount, MAX_PAGES);
        const cutOff = pageCount > MAX_PAGES;
        if (!cached) setTruncated(cutOff);

        if (lastPage > 1) {
          if (!cached) setLoadingRest(true);
          for (let start = 2; start <= lastPage; start += CONCURRENCY) {
            const batch: number[] = [];
            for (let p = start; p < start + CONCURRENCY && p <= lastPage; p++) {
              batch.push(p);
            }
            const pages = await Promise.all(
              batch.map((p) => fetchPageRef.current(params(p)))
            );
            if (!isCurrent()) return [];
            pages.forEach((page) => {
              const more = Array.isArray(page?.data) ? page!.data : [];
              all = dedupeAppend(all, more, rowIdRef.current);
            });
            // Progressive when building from nothing; a cached list is
            // swapped once, below, so it never shrinks on screen.
            if (!cached) setRows(all);
          }
        }

        if (cached) {
          setRows(all);
          setTotalCount(total);
          setTruncated(cutOff);
        }
        remember(fullKey, {
          rows: all,
          totalCount: total,
          truncated: cutOff,
          storedAt: Date.now(),
        });
        return all;
      } catch (e) {
        if (!isCurrent()) return [];
        console.error("Error loading history list:", e);
        // A failed revalidation keeps the cached rows and says nothing —
        // they were good a moment ago. A failed first load reports.
        if (!cached) {
          setError(
            getApiErrorMessage(
              e,
              all.length
                ? "Couldn't load all the records."
                : "Couldn't load this list."
            )
          );
        }
        return cached ? cached.rows : all;
      } finally {
        if (isCurrent()) {
          setLoading(false);
          setLoadingRest(false);
        }
      }
    },
    [scopeKey, fullKey]
  );

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const reload = useCallback(() => loadAll(true), [loadAll]);

  return {
    rows,
    loading,
    loadingRest,
    totalCount,
    truncated,
    error,
    reload,
  };
};

export default useHistoryList;

/** The banner shown while later pages are still arriving. */
export const historyLoadingRestText = (
  loaded: number,
  totalCount: number | null
): string => {
  const progress =
    typeof totalCount === "number" && totalCount > 0
      ? `${Math.min(loaded, totalCount).toLocaleString()} of ${totalCount.toLocaleString()} records loaded`
      : `${loaded.toLocaleString()} records loaded`;
  return `Loading the rest of the list — ${progress}. Filters are complete once this finishes.`;
};
